
import React, { useState, useCallback, useContext, useEffect } from "react";
import { toast } from "react-toastify";
import { add, format, parseISO } from "date-fns";

import Menu from "@material-ui/core/Menu";
import MenuItem from "@material-ui/core/MenuItem";
import PopupState, { bindTrigger, bindMenu } from "material-ui-popup-state";
// import { SocketContext } from "../../context/Socket/SocketContext";
import { makeStyles } from "@material-ui/core/styles";
import { green } from "@material-ui/core/colors";
import {
  Button,
  TableBody,
  TableRow,
  TableCell,
  IconButton,
  Table,
  TableHead,
  Paper,
  Tooltip,
  Typography,
  CircularProgress,
  Box,
  Card,
  CardContent,
} from "@material-ui/core";
import { WhatsApp } from "@material-ui/icons";
import {
  Pencil,
  CheckCircle2,
  QrCode,
  Trash2,
  Facebook,
  Instagram,
  Wifi,
} from "lucide-react";

import MainContainer from "../../components/MainContainer";
import TableRowSkeleton from "../../components/TableRowSkeleton";

import api from "../../services/api";
import WhatsAppModal from "../../components/WhatsAppModal";
import FacebookModal from "../../components/FacebookModal";
import WabaModal from "../../components/WabaModal";
import WabaTemplateModal from "../../components/WabaTemplateModal";
import ConfirmationModal from "../../components/ConfirmationModal";
import QrcodeModal from "../../components/QrcodeModal";
import { i18n } from "../../translate/i18n";
import { WhatsAppsContext } from "../../context/WhatsApp/WhatsAppsContext";
import toastError from "../../errors/toastError";
import formatSerializedId from '../../utils/formatSerializedId';
import { AuthContext } from "../../context/Auth/AuthContext";
import usePlans from "../../hooks/usePlans";
import { useHistory } from "react-router-dom/cjs/react-router-dom.min";
import ForbiddenPage from "../../components/ForbiddenPage";
import { Can } from "../../components/Can";

const getConnectionDisplayStatus = (status) => {
  if (status === "qrcode" || status === "OPENING") return status;
  if (status === "SYNC_ERROR") return status;
  if (status === "STALE") return status;
  return "CONNECTED";
};

const formatStaleSince = (lastSeenAt) => {
  if (!lastSeenAt) return null;
  const diffMs = Date.now() - new Date(lastSeenAt).getTime();
  if (Number.isNaN(diffMs) || diffMs < 0) return null;
  const minutes = Math.floor(diffMs / 60000);
  if (minutes < 60) return `${minutes} min`;
  const hours = Math.floor(minutes / 60);
  const rest = minutes % 60;
  return rest > 0 ? `${hours}h ${rest}min` : `${hours}h`;
};

const useStyles = makeStyles((theme) => {
  const ds = theme.chatiaDS || {};
  const dsColors = ds.colors || {};
  const isDark = theme.palette.type === "dark";
  return {
    mainPaper: {
      flex: 1,
      minHeight: 0,
      padding: 0,
      overflow: "hidden",
      borderRadius: ds.radius?.md ?? 12,
      background: dsColors.surfaceDefault || (isDark ? "#11141b" : "#f7f8fa"),
      border: `1px solid ${dsColors.border || (isDark ? "#202632" : "#e8e8e8")}`,
      boxShadow: isDark
        ? "0 2px 12px rgba(0,0,0,0.35)"
        : "0 2px 12px rgba(15,23,42,0.06)",
      display: "flex",
      flexDirection: "column",
    },
    tableScroll: {
      flex: 1,
      minHeight: 0,
      height: "100%",
      maxHeight: "100%",
      overflowY: "auto",
      overflowX: "auto",
      ...theme.scrollbarStyles,
    },
    table: {
      borderCollapse: "separate",
      borderSpacing: 0,
      "& .MuiTableCell-root": {
        borderBottom: `1px solid ${dsColors.borderLight || (isDark ? "#1a1f29" : "#f0f0f0")}`,
        color: dsColors.textPrimary || (isDark ? "#e9edef" : "#111b21"),
        fontSize: 13.5,
        padding: "14px 16px",
      },
    },
    tableHead: {
      "& .MuiTableCell-head": {
        background: dsColors.surfaceDefault || (isDark ? "#11141b" : "#f7f8fa"),
        backgroundColor: dsColors.surfaceDefault || (isDark ? "#11141b" : "#f7f8fa"),
        color: dsColors.textSecondary || (isDark ? "#8696a0" : "#667781"),
        fontWeight: 700,
        fontSize: 11.5,
        textTransform: "uppercase",
        letterSpacing: 0.6,
        borderBottom: `1px solid ${dsColors.border || (isDark ? "#202632" : "#e8e8e8")}`,
        padding: "12px 16px",
        position: "sticky",
        top: 0,
        zIndex: 3,
      },
      "& .MuiTableCell-head:first-child": {
        borderTopLeftRadius: ds.radius?.md ?? 12,
      },
      "& .MuiTableCell-head:last-child": {
        borderTopRightRadius: ds.radius?.md ?? 12,
      },
    },
    tableRow: {
      transition: "background-color 0.18s ease",
      "&:hover": {
        backgroundColor: dsColors.surfaceHover || (isDark ? "#171b24" : "#f3f4f6"),
      },
      "&:last-child .MuiTableCell-root": {
        borderBottom: "none",
      },
    },
    customTableCell: {
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
    },
    channelIconWrap: {
      display: "inline-flex",
      alignItems: "center",
      justifyContent: "center",
      width: 36,
      height: 36,
      borderRadius: 10,
      background: isDark ? "rgba(255,255,255,0.04)" : "rgba(15,23,42,0.04)",
    },
    nameCell: {
      fontWeight: 600,
      color: dsColors.textPrimary || (isDark ? "#e9edef" : "#111b21"),
    },
    numberCell: {
      color: dsColors.textSecondary || (isDark ? "#8696a0" : "#667781"),
      fontFeatureSettings: '"tnum"',
      fontVariantNumeric: "tabular-nums",
    },
    lastUpdateCell: {
      color: dsColors.textSecondary || (isDark ? "#8696a0" : "#667781"),
      fontFeatureSettings: '"tnum"',
      fontVariantNumeric: "tabular-nums",
    },
    statusBadge: {
      display: "inline-flex",
      alignItems: "center",
      gap: 6,
      padding: "4px 10px",
      borderRadius: 999,
      fontSize: 11.5,
      fontWeight: 600,
      lineHeight: 1.4,
      letterSpacing: 0.2,
    },
    statusConnected: {
      background: isDark ? "rgba(76,175,80,0.15)" : "rgba(76,175,80,0.12)",
      color: isDark ? "#81c784" : "#2e7d32",
    },
    statusDisconnected: {
      background: isDark ? "rgba(229,57,53,0.18)" : "rgba(229,57,53,0.10)",
      color: isDark ? "#ef9a9a" : "#c62828",
    },
    statusQrcode: {
      background: isDark ? "rgba(33,150,243,0.18)" : "rgba(33,150,243,0.10)",
      color: isDark ? "#90caf9" : "#1565c0",
    },
    statusOpening: {
      background: isDark ? "rgba(255,193,7,0.18)" : "rgba(255,193,7,0.14)",
      color: isDark ? "#ffd54f" : "#a06600",
    },
    statusSyncError: {
      background: isDark ? "rgba(251,140,0,0.20)" : "rgba(251,140,0,0.12)",
      color: isDark ? "#ffcc80" : "#b45309",
    },
    statusStale: {
      background: isDark ? "rgba(251,140,0,0.20)" : "rgba(251,140,0,0.12)",
      color: isDark ? "#ffcc80" : "#b45309",
    },
    statusDot: {
      width: 8,
      height: 8,
      borderRadius: "50%",
      display: "inline-block",
      flexShrink: 0,
    },
    iconActionBtn: {
      borderRadius: 10,
      padding: 6,
      transition: "all 0.18s ease",
      color: dsColors.textSecondary || (isDark ? "#8696a0" : "#667781"),
      "&:hover": {
        background: isDark ? "rgba(255,255,255,0.06)" : "rgba(15,23,42,0.06)",
        color: dsColors.textPrimary || (isDark ? "#e9edef" : "#111b21"),
      },
    },
    deleteActionBtn: {
      borderRadius: 10,
      padding: 6,
      transition: "all 0.18s ease",
      color: dsColors.danger || "#e53935",
      "&:hover": {
        background: dsColors.dangerBg || "rgba(229,57,53,0.10)",
        color: dsColors.dangerHover || "#c62828",
      },
    },
    disconnectButton: {
      color: dsColors.danger || "#d32f2f",
      borderColor: isDark
        ? "rgba(239,154,154,0.55)"
        : "rgba(211,47,47,0.55)",
      backgroundColor: "transparent",
      "&:hover": {
        color: dsColors.dangerHover || (isDark ? "#ffcdd2" : "#b71c1c"),
        borderColor: dsColors.dangerHover || (isDark ? "#ef9a9a" : "#b71c1c"),
        backgroundColor: dsColors.dangerBg || "rgba(211,47,47,0.08)",
      },
    },
    tooltip: {
      backgroundColor: isDark ? "rgba(30,40,50,0.92)" : "#f5f5f9",
      color: isDark ? "#e9edef" : "rgba(0, 0, 0, 0.87)",
      fontSize: theme.typography.pxToRem(13),
      border: `1px solid ${isDark ? "rgba(255,255,255,0.08)" : "#dadde9"}`,
      maxWidth: 450,
      borderRadius: 10,
    },
    tooltipPopper: {
      textAlign: "center",
    },
    buttonProgress: {
      color: green[500],
    },
    importCard: {
      borderRadius: ds.radius?.md ?? 12,
      background: dsColors.surfaceDefault || (isDark ? "#11141b" : "#f7f8fa"),
      border: `1px solid ${dsColors.border || (isDark ? "#202632" : "#e8e8e8")}`,
      boxShadow: "none",
      marginBottom: 12,
    },
    actionsCell: {
      whiteSpace: "nowrap",
    },
    emptyState: {
      padding: "60px 16px",
      textAlign: "center",
      color: dsColors.textMuted || (isDark ? "#666" : "#999"),
      fontSize: 13.5,
    },
    pageHeader: {
      display: "flex",
      alignItems: "center",
      justifyContent: "space-between",
      gap: 12,
      flexWrap: "wrap",
      padding: "4px 4px 14px",
    },
    headerLeft: {
      display: "flex",
      alignItems: "center",
      gap: 12,
    },
    pageIconWrap: {
      display: "inline-flex",
      alignItems: "center",
      justifyContent: "center",
      width: 36,
      height: 36,
      borderRadius: 10,
      background: isDark ? "rgba(255,255,255,0.05)" : "rgba(0,0,0,0.04)",
      color: theme.palette.primary.main,
    },
    pageTitle: {
      fontSize: 18,
      fontWeight: 600,
      color: dsColors.textPrimary || (isDark ? "#e9edef" : "#1a1a1a"),
      lineHeight: 1.2,
      display: "inline-flex",
      alignItems: "center",
      gap: 10,
      margin: 0,
    },
    pageSubtitle: {
      fontSize: 12,
      color:
        dsColors.textSecondary ||
        (isDark ? "rgba(255,255,255,0.55)" : "rgba(0,0,0,0.55)"),
      marginTop: 2,
    },
    pageCountChip: {
      display: "inline-flex",
      alignItems: "center",
      padding: "2px 9px",
      borderRadius: 999,
      fontSize: 12,
      fontWeight: 700,
      letterSpacing: 0.2,
      background: isDark ? "rgba(255,255,255,0.06)" : "rgba(15,23,42,0.06)",
      color: dsColors.textSecondary || (isDark ? "#8696a0" : "#667781"),
    },
    pageActions: {
      display: "flex",
      flexDirection: "row",
      alignItems: "center",
      gap: 8,
      flexWrap: "wrap",
    },
    newConnectionMenu: {
      "& .MuiPaper-root": {
        borderRadius: 12,
        marginTop: 6,
        minWidth: 240,
        background: isDark ? "#11141b" : "#ffffff",
        border: `1px solid ${dsColors.border || (isDark ? "#202632" : "#e8e8e8")}`,
        boxShadow: isDark
          ? "0 8px 24px rgba(0,0,0,0.45)"
          : "0 8px 24px rgba(15,23,42,0.10)",
      },
      "& .MuiList-root": {
        padding: 4,
      },
      "& .MuiMenuItem-root": {
        borderRadius: 8,
        fontSize: 14,
        padding: "8px 12px",
        margin: "1px 0",
        color: dsColors.textPrimary || (isDark ? "#e9edef" : "#111b21"),
        transition: "background-color 0.15s ease",
        "&:hover": {
          backgroundColor: isDark ? "rgba(255,255,255,0.06)" : "rgba(15,23,42,0.05)",
        },
        "&.Mui-disabled": {
          opacity: 0.5,
        },
      },
    },
    menuItemContent: {
      display: "flex",
      alignItems: "center",
      gap: 10,
      width: "100%",
    },
    menuItemLabel: {
      flex: 1,
    },
    betaBadge: {
      display: "inline-flex",
      alignItems: "center",
      padding: "2px 8px",
      borderRadius: 999,
      fontSize: 10,
      fontWeight: 700,
      letterSpacing: 0.6,
      textTransform: "uppercase",
      background: isDark ? "rgba(255,193,7,0.18)" : "rgba(255,193,7,0.20)",
      color: isDark ? "#ffd54f" : "#a06600",
      lineHeight: 1.2,
    },
  };
});

function CircularProgressWithLabel(props) {
  return (
    <Box position="relative" display="inline-flex">
      <CircularProgress variant="determinate" {...props} />
      <Box
        top={0}
        left={0}
        bottom={0}
        right={0}
        position="absolute"
        display="flex"
        alignItems="center"
        justifyContent="center"
      >
        <Typography
          variant="caption"
          component="div"
          color="textSecondary"
        >{`${Math.round(props.value)}%`}</Typography>
      </Box>
    </Box>
  );
}

const CustomToolTip = ({ title, content, children }) => {
  const classes = useStyles();

  return (
    <Tooltip
      arrow
      classes={{
        tooltip: classes.tooltip,
        popper: classes.tooltipPopper,
      }}
      title={
        <React.Fragment>
          <Typography gutterBottom color="inherit">
            {title}
          </Typography>
          {content && <Typography>{content}</Typography>}
        </React.Fragment>
      }
    >
      {children}
    </Tooltip>
  );
};

// Mascara os últimos 4 caracteres do número formatado (ex: "🇧🇷 (11) 98765-4321" → "🇧🇷 (11) 98765-****")
const maskLast4 = (value) => {
  if (!value || typeof value !== "string") return value;
  if (value.length <= 4) return "****";
  return value.slice(0, -4) + "****";
};

const IconChannel = (channel, provider) => {
  if (provider === "official" || channel === "whatsapp_official") {
    return <WhatsApp style={{ color: "#128C7E", fontSize: 20 }} />;
  }

  switch (channel) {
    case "facebook":
      return <Facebook size={20} color="#3b5998" strokeWidth={2} />;
    case "instagram":
      return <Instagram size={20} color="#e1306c" strokeWidth={2} />;
    case "whatsapp":
      return <WhatsApp style={{ color: "#25d366", fontSize: 20 }} />;
    default:
      return i18n.t("connections.iconChannel.error");
  }
};

const Connections = () => {
  const classes = useStyles();

  const { whatsApps, loading } = useContext(WhatsAppsContext);
  const [whatsAppModalOpen, setWhatsAppModalOpen] = useState(false);
  const [statusImport, setStatusImport] = useState([]);
  const [qrModalOpen, setQrModalOpen] = useState(false);
  const [selectedWhatsApp, setSelectedWhatsApp] = useState(null);
  const [newWhatsAppChannel, setNewWhatsAppChannel] = useState("whatsapp");
  const [confirmModalOpen, setConfirmModalOpen] = useState(false);
  const history = useHistory();
  const confirmationModalInitialState = {
    action: "",
    title: "",
    message: "",
    whatsAppId: "",
    open: false,
  };
  const [confirmModalInfo, setConfirmModalInfo] = useState(confirmationModalInitialState);
  const [planConfig, setPlanConfig] = useState(false);
  const [facebookModalOpen, setFacebookModalOpen] = useState(false);
  const [facebookModalChannel, setFacebookModalChannel] = useState("facebook");
  const [wabaModalOpen, setWabaModalOpen] = useState(false);
  const [wabaTemplateModalOpen, setWabaTemplateModalOpen] = useState(false);
  const [wabaCredentialsReady, setWabaCredentialsReady] = useState(false);

  //   const socketManager = useContext(SocketContext);
  const { user, socket } = useContext(AuthContext);

  const companyId = user.companyId;

  const { getPlanCompany } = usePlans();

  const loadWabaCredentialsStatus = useCallback(async () => {
    try {
      const { data } = await api.get("/integrations/meta/credentials");
      const waba = data?.waba || {};
      setWabaCredentialsReady(
        Boolean(
          waba.facebookAppId &&
          waba.facebookAppSecret &&
          waba.facebookConfigId &&
          waba.whatsappBusinessToken &&
          waba.wabaVerifyToken
        )
      );
    } catch (err) {
      setWabaCredentialsReady(false);
    }
  }, []);

  useEffect(() => {
    async function fetchData() {
      const planConfigs = await getPlanCompany(undefined, companyId);
      setPlanConfig(planConfigs)
    }
    fetchData();
    loadWabaCredentialsStatus();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleOpenFacebookModal = (channel) => {
    setFacebookModalChannel(channel);
    setFacebookModalOpen(true);
  };

  const handleCloseFacebookModal = () => {
    setFacebookModalOpen(false);
  };

  useEffect(() => {
    // const socket = socketManager.GetSocket();
    socket.on(`importMessages-${user.companyId}`, (data) => {
      if (data.action === "refresh") {
        setStatusImport([]);
        history.go(0);
      }
      if (data.action === "update") {
        setStatusImport(data.status);
      }
    });

    /* return () => {
      socket.disconnect();
    }; */
  }, [whatsApps]);

  const handleStartWhatsAppSession = async (whatsAppId) => {
    try {
      await api.post(`/whatsappsession/${whatsAppId}`);
    } catch (err) {
      toastError(err);
    }
  };

  const handleRequestNewQrCode = async (whatsAppId) => {
    try {
      await api.put(`/whatsappsession/${whatsAppId}`);
    } catch (err) {
      toastError(err);
    }
  };

  const handleOpenWhatsAppModal = (channel = "whatsapp") => {
    setSelectedWhatsApp(null);
    setNewWhatsAppChannel(channel);
    setWhatsAppModalOpen(true);
  };

  const handleCloseWhatsAppModal = useCallback(() => {
    setWhatsAppModalOpen(false);
    setSelectedWhatsApp(null);
    setNewWhatsAppChannel("whatsapp");
  }, [setSelectedWhatsApp, setWhatsAppModalOpen]);

  const handleOpenQrModal = (whatsApp) => {
    setSelectedWhatsApp(whatsApp);
    setQrModalOpen(true);
  };

  const handleCloseQrModal = useCallback(() => {
    setSelectedWhatsApp(null);
    setQrModalOpen(false);
  }, [setQrModalOpen, setSelectedWhatsApp]);

  const handleEditWhatsApp = (whatsApp) => {
    setSelectedWhatsApp(whatsApp);
    setNewWhatsAppChannel(whatsApp?.channel || "whatsapp");
    setWhatsAppModalOpen(true);
  };

  const handleOpenWabaTemplateModal = (whatsApp) => {
    setSelectedWhatsApp(whatsApp);
    setWabaTemplateModalOpen(true);
  };

  const handleCloseWabaTemplateModal = () => {
    setWabaTemplateModalOpen(false);
    setSelectedWhatsApp(null);
  };

  const handleCloseWabaModal = () => {
    setWabaModalOpen(false);
    loadWabaCredentialsStatus();
  };

  const handleOpenConfirmationModal = (action, whatsAppId) => {
    if (action === "disconnect") {
      setConfirmModalInfo({
        action: action,
        title: i18n.t("connections.confirmationModal.disconnectTitle"),
        message: i18n.t("connections.confirmationModal.disconnectMessage"),
        whatsAppId: whatsAppId,
      });
    }

    if (action === "delete") {
      setConfirmModalInfo({
        action: action,
        title: i18n.t("connections.confirmationModal.deleteTitle"),
        message: i18n.t("connections.confirmationModal.deleteMessage"),
        whatsAppId: whatsAppId,
      });
    }
    if (action === "closedImported") {
      setConfirmModalInfo({
        action: action,
        title: i18n.t("connections.confirmationModal.closedImportedTitle"),
        message: i18n.t("connections.confirmationModal.closedImportedMessage"),
        whatsAppId: whatsAppId,
      });
    }
    setConfirmModalOpen(true);
  };

  const handleSubmitConfirmationModal = async () => {
    if (confirmModalInfo.action === "disconnect") {
      try {
        await api.delete(`/whatsappsession/${confirmModalInfo.whatsAppId}`);
      } catch (err) {
        toastError(err);
      }
    }

    if (confirmModalInfo.action === "delete") {
      try {
        await api.delete(`/whatsapp/${confirmModalInfo.whatsAppId}`);
        toast.success(i18n.t("connections.toasts.deleted"));
      } catch (err) {
        toastError(err);
      }
    }
    if (confirmModalInfo.action === "closedImported") {
      try {
        await api.post(`/closedimported/${confirmModalInfo.whatsAppId}`);
        toast.success(i18n.t("connections.toasts.closedimported"));
      } catch (err) {
        toastError(err);
      }
    }

    setConfirmModalInfo(confirmationModalInitialState);
  };


  const renderImportButton = (whatsApp) => {
    if (whatsApp?.statusImportMessages === "renderButtonCloseTickets") {
      return (
        <Button
          style={{ marginLeft: 12 }}
          size="small"
          variant="outlined"
          color="primary"
          onClick={() => {
            handleOpenConfirmationModal("closedImported", whatsApp.id);
          }}
        >
          {i18n.t("connections.buttons.closedImported")}
        </Button>
      );
    }

    if (whatsApp?.importOldMessages) {
      let isTimeStamp = !isNaN(
        new Date(Math.floor(whatsApp?.statusImportMessages)).getTime()
      );

      if (isTimeStamp) {
        const ultimoStatus = new Date(
          Math.floor(whatsApp?.statusImportMessages)
        ).getTime();
        const dataLimite = +add(ultimoStatus, { seconds: +35 }).getTime();
        if (dataLimite > new Date().getTime()) {
          return (
            <>
              <Button
                disabled
                style={{ marginLeft: 12 }}
                size="small"
                endIcon={
                  <CircularProgress
                    size={12}
                    className={classes.buttonProgress}
                  />
                }
                variant="outlined"
                color="primary"
              >
                {i18n.t("connections.buttons.preparing")}
              </Button>
            </>
          );
        }
      }
    }
  };

  const renderActionButtons = (whatsApp) => {
    if (whatsApp.provider === "official") {
      return (
        <Can
          role={user.profile}
          perform="connections-page:addConnection"
          yes={() => (
            <>
              <Button
                size="small"
                variant="outlined"
                color="primary"
                onClick={() => handleOpenWabaTemplateModal(whatsApp)}
              >
                Templates
              </Button>{" "}
              <Button
                size="small"
                variant="outlined"
                className={classes.disconnectButton}
                onClick={() => handleOpenConfirmationModal("delete", whatsApp.id)}
              >
                {i18n.t("connections.buttons.disconnect")}
              </Button>
            </>
          )}
        />
      );
    }

    const displayStatus = getConnectionDisplayStatus(whatsApp.status);

    return (
      <>
        {displayStatus === "qrcode" && (
          <Can
            role={user.profile === "user" && user.allowConnections === "enabled" ? "admin" : user.profile}
            perform="connections-page:addConnection"
            yes={() => (
              <Button
                size="small"
                variant="contained"
                color="primary"
                onClick={() => handleOpenQrModal(whatsApp)}
              >
                {i18n.t("connections.buttons.qrcode")}
              </Button>
            )}
          />
        )}
        {displayStatus === "DISCONNECTED" && (
          <Can
            role={user.profile === "user" && user.allowConnections === "enabled" ? "admin" : user.profile}
            perform="connections-page:addConnection"
            yes={() => (
              <>
                <Button
                  size="small"
                  variant="outlined"
                  color="primary"
                  onClick={() => handleStartWhatsAppSession(whatsApp.id)}
                >
                  {i18n.t("connections.buttons.tryAgain")}
                </Button>{" "}
                <Button
                  size="small"
                  variant="outlined"
                  color="secondary"
                  onClick={() => handleRequestNewQrCode(whatsApp.id)}
                >
                  {i18n.t("connections.buttons.newQr")}
                </Button>
              </>
            )}
          />
        )}
        {displayStatus === "CONNECTED" && (
            <Can
              role={user.profile}
              perform="connections-page:addConnection"
              yes={() => (
                <>
                  <Button
                    size="small"
                    variant="outlined"
                    className={classes.disconnectButton}
                    onClick={() => {
                      handleOpenConfirmationModal("disconnect", whatsApp.id);
                    }}
                  >
                    {i18n.t("connections.buttons.disconnect")}
                  </Button>

                  {renderImportButton(whatsApp)}
                </>
              )}
            />
          )}
        {displayStatus === "OPENING" && (
          <Button size="small" variant="outlined" disabled color="default">
            {i18n.t("connections.buttons.connecting")}
          </Button>
        )}
        {displayStatus === "SYNC_ERROR" && (
          <Can
            role={user.profile === "user" && user.allowConnections === "enabled" ? "admin" : user.profile}
            perform="connections-page:addConnection"
            yes={() => (
              <Button
                size="small"
                variant="outlined"
                color="primary"
                onClick={() => handleStartWhatsAppSession(whatsApp.id)}
              >
                {i18n.t("connections.buttons.tryAgain")}
              </Button>
            )}
          />
        )}
        {displayStatus === "STALE" && (
          <Can
            role={user.profile === "user" && user.allowConnections === "enabled" ? "admin" : user.profile}
            perform="connections-page:addConnection"
            yes={() => (
              <>
                <Button
                  size="small"
                  variant="contained"
                  color="primary"
                  onClick={() => handleStartWhatsAppSession(whatsApp.id)}
                >
                  {i18n.t("connections.buttons.reconnect") || "Reconectar"}
                </Button>{" "}
                <Button
                  size="small"
                  variant="outlined"
                  className={classes.disconnectButton}
                  onClick={() => handleOpenConfirmationModal("disconnect", whatsApp.id)}
                >
                  {i18n.t("connections.buttons.disconnect")}
                </Button>
              </>
            )}
          />
        )}
      </>
    );
  };

  const renderStatusBadge = (whatsApp) => {
    if (whatsApp.provider === "official") {
      return (
        <CustomToolTip title={i18n.t("connections.toolTips.connected.title")}>
          <span className={`${classes.statusBadge} ${classes.statusConnected}`}>
            <span
              className={classes.statusDot}
              style={{ background: "#4caf50" }}
            />
            {i18n.t("connections.status.connected")}
          </span>
        </CustomToolTip>
      );
    }

    const status = getConnectionDisplayStatus(whatsApp.status);

    if (status === "OPENING") {
      return (
        <span className={`${classes.statusBadge} ${classes.statusOpening}`}>
          <CircularProgress size={10} className={classes.buttonProgress} />
          {i18n.t("connections.buttons.connecting")}
        </span>
      );
    }

    if (status === "SYNC_ERROR") {
      return (
        <CustomToolTip
          title={i18n.t("connections.toolTips.syncError.title") || "Falha ao processar mensagens"}
          content={i18n.t("connections.toolTips.syncError.content") || "A sessão está conectada, mas o sistema falhou ao gravar dados da conexão. Reinicie a conexão e verifique novamente."}
        >
          <span className={`${classes.statusBadge} ${classes.statusSyncError}`}>
            <span
              className={classes.statusDot}
              style={{ background: "#fb8c00" }}
            />
            {i18n.t("connections.status.syncError") || "Falha de sincronização"}
          </span>
        </CustomToolTip>
      );
    }

    if (status === "STALE") {
      const since = formatStaleSince(whatsApp.lastSeenAt);
      return (
        <CustomToolTip
          title={i18n.t("connections.toolTips.stale.title") || "Conexão sem resposta"}
          content={
            i18n.t("connections.toolTips.stale.content") ||
            `O WhatsApp segue logado, mas não recebe mensagens${since ? ` há ${since}` : ""}. Clique em "Iniciar Sessão" para forçar reconexão (sem novo QR).`
          }
        >
          <span className={`${classes.statusBadge} ${classes.statusStale}`}>
            <span
              className={classes.statusDot}
              style={{ background: "#fb8c00" }}
            />
            {(i18n.t("connections.status.stale") || "Sem resposta")}
            {since ? ` · ${since}` : ""}
          </span>
        </CustomToolTip>
      );
    }

    if (status === "CONNECTED") {
      return (
        <CustomToolTip title={i18n.t("connections.toolTips.connected.title")}>
          <span className={`${classes.statusBadge} ${classes.statusConnected}`}>
            <span
              className={classes.statusDot}
              style={{ background: "#4caf50" }}
            />
            {i18n.t("connections.status.connected")}
          </span>
        </CustomToolTip>
      );
    }

    if (status === "DISCONNECTED") {
      return (
        <CustomToolTip
          title={i18n.t("connections.toolTips.disconnected.title")}
          content={i18n.t("connections.toolTips.disconnected.content")}
        >
          <span className={`${classes.statusBadge} ${classes.statusDisconnected}`}>
            <span
              className={classes.statusDot}
              style={{ background: "#e53935" }}
            />
            {i18n.t("connections.status.disconnected")}
          </span>
        </CustomToolTip>
      );
    }

    if (status === "qrcode") {
      return (
        <CustomToolTip
          title={i18n.t("connections.toolTips.qrcode.title")}
          content={i18n.t("connections.toolTips.qrcode.content")}
        >
          <span className={`${classes.statusBadge} ${classes.statusQrcode}`}>
            <QrCode size={13} strokeWidth={2} />
            {i18n.t("connections.status.qrcode")}
          </span>
        </CustomToolTip>
      );
    }

    if (status === "TIMEOUT" || status === "PAIRING") {
      return (
        <CustomToolTip
          title={i18n.t("connections.toolTips.timeout.title")}
          content={i18n.t("connections.toolTips.timeout.content")}
        >
          <span className={`${classes.statusBadge} ${classes.statusOpening}`}>
            <span
              className={classes.statusDot}
              style={{ background: "#ffc107" }}
            />
            {status === "PAIRING"
              ? i18n.t("connections.status.pairing")
              : i18n.t("connections.status.timeout")}
          </span>
        </CustomToolTip>
      );
    }

    return null;
  };

  const restartWhatsapps = async () => {

    try {
      await api.post(`/whatsapp-restart/`);
      toast.success(i18n.t("connections.waitConnection"));
    } catch (err) {
      toastError(err);
    }
  }

  return (
    <MainContainer>
      <ConfirmationModal
        title={confirmModalInfo.title}
        open={confirmModalOpen}
        onClose={setConfirmModalOpen}
        onConfirm={handleSubmitConfirmationModal}
      >
        {confirmModalInfo.message}
      </ConfirmationModal>
      {qrModalOpen && (
        <QrcodeModal
          open={qrModalOpen}
          onClose={handleCloseQrModal}
          whatsAppId={!whatsAppModalOpen && selectedWhatsApp?.id}
        />
      )}
      <WhatsAppModal
        open={whatsAppModalOpen}
        onClose={handleCloseWhatsAppModal}
        whatsAppId={!qrModalOpen && selectedWhatsApp?.id}
        initialChannel={newWhatsAppChannel}
      />
      <FacebookModal
        open={facebookModalOpen}
        onClose={handleCloseFacebookModal}
        channel={facebookModalChannel}
      />
      <WabaModal
        open={wabaModalOpen}
        onClose={handleCloseWabaModal}
      />
      <WabaTemplateModal
        open={wabaTemplateModalOpen}
        onClose={handleCloseWabaTemplateModal}
        whatsapp={selectedWhatsApp}
      />
      {user.profile === "user" && user.allowConnections === "disabled" ?
        <ForbiddenPage />
        :
        <>
          <div className={classes.pageHeader}>
            <div className={classes.headerLeft}>
              <span className={classes.pageIconWrap}>
                <Wifi size={20} strokeWidth={1.8} />
              </span>
              <div>
                <h1 className={classes.pageTitle}>
                  {i18n.t("connections.title")}
                  <span className={classes.pageCountChip}>{whatsApps.length}</span>
                </h1>
                <div className={classes.pageSubtitle}>
                  {i18n.t("connections.subtitle") || "Gerencie seus canais de atendimento"}
                </div>
              </div>
            </div>
            <div className={classes.pageActions}>
              <Button
                variant="contained"
                color="primary"
                onClick={restartWhatsapps}
              >
                {i18n.t("connections.restartConnections")}
              </Button>

              <PopupState variant="popover" popupId="demo-popup-menu">
                {(popupState) => (
                  <React.Fragment>
                    <Can
                      role={user.profile}
                      perform="connections-page:addConnection"
                      yes={() => (
                        <>
                          <Button
                            variant="contained"
                            color="primary"
                            {...bindTrigger(popupState)}
                          >
                            {i18n.t("connections.newConnection")}
                          </Button>
                          <Menu
                            {...bindMenu(popupState)}
                            className={classes.newConnectionMenu}
                            getContentAnchorEl={null}
                            anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
                            transformOrigin={{ vertical: "top", horizontal: "right" }}
                          >
                            {/* WHATSAPP */}
                            <MenuItem
                              disabled={planConfig?.plan?.useWhatsapp ? false : true}
                              onClick={() => {
                                handleOpenWhatsAppModal();
                                popupState.close();
                              }}
                            >
                              <span className={classes.menuItemContent}>
                                <WhatsApp
                                  fontSize="small"
                                  style={{ color: "#25D366" }}
                                />
                                <span className={classes.menuItemLabel}>
                                  {i18n.t("channels.whatsapp")}
                                </span>
                              </span>
                            </MenuItem>
                            {/* WHATSAPP API OFICIAL */}
                            <CustomToolTip
                              title={
                                wabaCredentialsReady
                                  ? "WhatsApp API Oficial"
                                  : "Preencha todas as credenciais em Configurações > Meta"
                              }
                            >
                              <span>
                                <MenuItem
                                  disabled={!wabaCredentialsReady}
                                  onClick={() => {
                                    if (!wabaCredentialsReady) return;
                                    setWabaModalOpen(true);
                                    popupState.close();
                                  }}
                                >
                                  <span className={classes.menuItemContent}>
                                    <WhatsApp
                                      fontSize="small"
                                      style={{ color: "#128C7E" }}
                                    />
                                    <span className={classes.menuItemLabel}>
                                      {i18n.t("channels.whatsappOfficial")}
                                    </span>
                                  </span>
                                </MenuItem>
                              </span>
                            </CustomToolTip>
                            {/* FACEBOOK */}
                            <MenuItem
                              disabled={planConfig?.plan?.useFacebook ? false : true}
                              onClick={() => {
                                handleOpenFacebookModal("facebook");
                                popupState.close();
                              }}
                            >
                              <span className={classes.menuItemContent}>
                                <Facebook size={18} color="#3b5998" strokeWidth={2} />
                                <span className={classes.menuItemLabel}>
                                  {i18n.t("channels.facebook")}
                                </span>
                              </span>
                            </MenuItem>
                            {/* INSTAGRAM */}
                            <MenuItem
                              disabled={planConfig?.plan?.useInstagram ? false : true}
                              onClick={() => {
                                handleOpenFacebookModal("instagram");
                                popupState.close();
                              }}
                            >
                              <span className={classes.menuItemContent}>
                                <Instagram size={18} color="#e1306c" strokeWidth={2} />
                                <span className={classes.menuItemLabel}>
                                  {i18n.t("channels.instagram")}
                                </span>
                              </span>
                            </MenuItem>
                          </Menu>
                        </>
                      )}
                    />
                  </React.Fragment>
                )}
              </PopupState>
            </div>
          </div>

          {
            statusImport?.all ? (
              <>
                <div style={{ margin: "auto", marginBottom: 12 }}>
                  <Card className={classes.importCard} elevation={0}>
                    <CardContent>
                      <Typography component="h5" variant="h5">

                        {statusImport?.this === -1 ? i18n.t("connections.buttons.preparing") : i18n.t("connections.buttons.importing")}

                      </Typography>
                      {statusImport?.this === -1 ?
                        <Typography component="h6" variant="h6" align="center">

                          <CircularProgress
                            size={24}
                          />

                        </Typography>
                        :
                        <>
                          <Typography component="h6" variant="h6" align="center">
                            {`${i18n.t(`connections.typography.processed`)} ${statusImport?.this} ${i18n.t(`connections.typography.in`)} ${statusImport?.all}  ${i18n.t(`connections.typography.date`)}: ${statusImport?.date} `}
                          </Typography>
                          <Typography align="center">
                            <CircularProgressWithLabel
                              style={{ margin: "auto" }}
                              value={(statusImport?.this / statusImport?.all) * 100}
                            />
                          </Typography>
                        </>
                      }
                    </CardContent>
                  </Card>
                </div>
              </>
            ) : null
          }

          <Paper className={classes.mainPaper} variant="outlined" elevation={0}>
            <div className={classes.tableScroll}>
              <Table size="small" stickyHeader className={classes.table}>
                <TableHead className={classes.tableHead}>
                  <TableRow>
                    <TableCell align="center" style={{ width: 80 }}>{i18n.t("connections.table.channel")}</TableCell>
                    <TableCell align="left">{i18n.t("connections.table.name")}</TableCell>
                    <TableCell align="left">{i18n.t("connections.table.number")}</TableCell>
                    <TableCell align="center">{i18n.t("connections.table.status")}</TableCell>
                    <TableCell align="center">{i18n.t("connections.table.session")}</TableCell>
                    <TableCell align="center">{i18n.t("connections.table.lastUpdate")}</TableCell>
                    <TableCell align="center">{i18n.t("connections.table.default")}</TableCell>
                    <Can
                      role={user.profile === "user" && user.allowConnections === "enabled" ? "admin" : user.profile}
                      perform="connections-page:addConnection"
                      yes={() => (
                        <TableCell align="center">{i18n.t("connections.table.actions")}</TableCell>
                      )}
                    />
                  </TableRow>
                </TableHead>
                <TableBody>
                  {loading ? (
                    <TableRowSkeleton />
                  ) : (
                    <>
                      {whatsApps?.length > 0 ? (
                        whatsApps.map((whatsApp) => (
                          <TableRow key={whatsApp.id} className={classes.tableRow} hover>
                            <TableCell align="center">
                              <span className={classes.channelIconWrap}>
                                {IconChannel(whatsApp.channel, whatsApp.provider)}
                              </span>
                            </TableCell>
                            <TableCell align="left" className={classes.nameCell}>{whatsApp.name}</TableCell>
                            <TableCell align="left" className={classes.numberCell}>
                              {whatsApp.number && whatsApp.channel === 'whatsapp'
                                ? maskLast4(formatSerializedId(whatsApp.number))
                                : maskLast4(whatsApp.number)}
                            </TableCell>
                            <TableCell align="center">{renderStatusBadge(whatsApp)}</TableCell>
                            <TableCell align="center">{renderActionButtons(whatsApp)}</TableCell>
                            <TableCell align="center" className={classes.lastUpdateCell}>
                              {format(parseISO(whatsApp.updatedAt), "dd/MM/yy HH:mm")}
                            </TableCell>
                            <TableCell align="center">
                              {whatsApp.isDefault && (
                                <div className={classes.customTableCell}>
                                  <CheckCircle2 size={20} color={green[500]} strokeWidth={2} />
                                </div>
                              )}
                            </TableCell>
                            <Can
                              role={user.profile}
                              perform="connections-page:addConnection"
                              yes={() => (
                                <TableCell align="center" className={classes.actionsCell}>
                                  <Tooltip title={i18n.t("connections.buttons.edit") || "Editar"}>
                                    <IconButton
                                      size="small"
                                      className={classes.iconActionBtn}
                                      onClick={() => handleEditWhatsApp(whatsApp)}
                                    >
                                      <Pencil size={16} strokeWidth={2} />
                                    </IconButton>
                                  </Tooltip>
                                  <Tooltip title={i18n.t("connections.buttons.delete") || "Excluir"}>
                                    <IconButton
                                      size="small"
                                      className={classes.deleteActionBtn}
                                      onClick={() => {
                                        handleOpenConfirmationModal("delete", whatsApp.id);
                                      }}
                                    >
                                      <Trash2 size={16} strokeWidth={2} />
                                    </IconButton>
                                  </Tooltip>
                                </TableCell>
                              )}
                            />
                          </TableRow>
                        ))
                      ) : (
                        <TableRow>
                          <TableCell colSpan={8} className={classes.emptyState}>
                            {i18n.t("connections.empty") || "Nenhuma conexão cadastrada."}
                          </TableCell>
                        </TableRow>
                      )}
                    </>
                  )}
                </TableBody>
              </Table>
            </div>
          </Paper>
        </>
      }
    </MainContainer >

  );
};

export default Connections;
